import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import type { Environment } from '../contentstackEnvironment';
import { stackConfig } from '../stackConfig';
import type { StackName } from '../types';
import type { ContentstackOnly } from './url';
import { getContentstackURL } from './url';

const DEFAULT_CLIENT_TIMEOUT = 2000;

type ContentstackEntry = Record<string, unknown>;
type ContentstackQuery = Record<string, unknown>;

interface ContentstackEntries {
  entries: ContentstackEntry[];
}

export interface ContentstackParams {
  include?: string[];
  only?: ContentstackOnly[];
}

export interface CommonFetchStackArgs {
  branch?: string;
  contentType: string;
  env: Environment;
  include?: string[];
  only?: ContentstackOnly[];
  stackName: StackName;
  timeout?: number;
}

interface FetchFromContentstackArgs {
  branch?: string;
  env: Environment;
  stackName: StackName;
  timeout?: number;
  url: string;
}

const fetchFromContentstack = ({
  branch,
  env,
  stackName,
  timeout = DEFAULT_CLIENT_TIMEOUT,
  url,
}: FetchFromContentstackArgs) => {
  const headers = {
    api_key: stackConfig[stackName].apiKey,
    access_token: stackConfig[stackName].envs[env].token,
    ...(branch ? { branch } : {}),
  };

  return fetchJSONWithTimeout(url, {
    headers,
    timeout,
  });
};

const contentNotPublishedStatusCode = 422;

export const fetchStackEntry = async ({
  branch,
  contentType,
  entryId,
  env,
  include = [],
  only = [],
  stackName,
  timeout,
}: CommonFetchStackArgs & { entryId: string }) => {
  const url = getContentstackURL({
    contentType,
    entryId,
    env: stackConfig[stackName].envs[env].name,
    include,
    only,
  });

  try {
    const response = await fetchFromContentstack({ branch, env, stackName, timeout, url });

    if (response.status === contentNotPublishedStatusCode) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`failed to get content by entry ID: url ${url} status:${response.status}`);
    }

    const data: ContentstackEntry = await response.json();

    return data.entry;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchStackQuery = async ({
  branch,
  contentType,
  env,
  include = [],
  only = [],
  query,
  stackName,
  timeout,
}: CommonFetchStackArgs & { query: ContentstackQuery }) => {
  const url = getContentstackURL({
    contentType,
    env: stackConfig[stackName].envs[env].name,
    query,
    include,
    only,
  });

  try {
    const response = await fetchFromContentstack({ branch, env, stackName, timeout, url });

    if (!response.ok) {
      throw new Error(`failed to get content by query: url ${url} status:${response.status}`);
    }

    const data: ContentstackEntries = await response.json();

    return (Array.isArray(data.entries) && data.entries[0]) || null;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchStackEntries = async ({
  asc,
  branch,
  contentType,
  env,
  include = [],
  limit,
  only = [],
  query,
  skip,
  stackName,
  timeout,
}: CommonFetchStackArgs & {
  asc?: string;
  limit?: number;
  query: ContentstackQuery;
  skip?: number;
}): Promise<ContentstackEntry[]> => {
  const url = getContentstackURL({
    asc,
    contentType,
    env: stackConfig[stackName].envs[env].name,
    limit,
    query,
    skip,
    include,
    only,
  });

  try {
    const response = await fetchFromContentstack({ branch, env, stackName, timeout, url });

    if (!response.ok) {
      throw new Error(
        `failed to get content entries by query: url ${url} status:${response.status}`,
      );
    }

    const data: ContentstackEntries = await response.json();

    return Array.isArray(data.entries) ? data.entries : [];
  } catch (error) {
    return Promise.reject(error);
  }
};
